import React, { useEffect } from 'react';

import { useTranslation } from 'react-i18next';

import { bemCssModules, getTestId } from '@utils';
import { useUpdateWalletLimits, useLocation, useNavigate } from '@utils/hooks';
import { LocationStateType } from '@utils/types';

import { useStore, buyActions } from '@modules';
import { CryptoCurrencyEnum } from '@modules/global/types';
import { OperationsEnum } from '@modules/wallet/types';

import Button from '@components/Button';
import Converter, { TConverter } from '@containers/Converter';
import Limits from '@containers/Converter/Limits';
import useConverterState from '@containers/Converter/useConverterState';
import { useCustomContext } from '@containers/OperationBlock/context';
import ZHDisclaimerInfo from '@containers/ZHDisclaimerInfo';

import Details from '../../../Details';
import OperationsDisabledDisclaimer from '../../OperationsDisabledDisclaimer';

import style from './BuyWithNewCard.scss';

const cn = bemCssModules(style, 'BuyWithNewCard');
const ti = getTestId('BuyWithNewCard');

const BuyWithNewCard: CustomFC = ({ className }) => {
  const { t } = useTranslation();

  const [{ buy, global, wallet }, dispatch] = useStore();
  const navigate = useNavigate();
  const { currencies } = global;
  const { right } = useCustomContext();

  const location = useLocation<LocationStateType['wallets']>();
  const { state } = location;
  const { cacheConverter } = state || {};

  const converterState = useConverterState({
    fromCurrencies: currencies.fiat,
    toCurrencies: [right?.value as CryptoCurrencyEnum].filter(cur =>
      currencies.crypto.includes(cur)
    ),
    cacheState: cacheConverter,
  });
  const { fromCurrency, fromAmount, toCurrency, rate, showLimits } = converterState;

  const { setSelectedCurrency } = useCustomContext();

  useEffect(() => {
    if (toCurrency) setSelectedCurrency(toCurrency);
  }, [toCurrency]);

  useUpdateWalletLimits({
    crypto_currency: toCurrency,
    currency: fromCurrency,
    type: OperationsEnum.buy,
    shouldStop: !showLimits,
  });

  const onSubmit = () => {
    if (CONST.operationsDisabled) return;
    navigate(CONST.nav.operationWithNewCard);
  };

  const getRate: TConverter['getRate'] = (from, to, amount) =>
    dispatch(buyActions.getBuyRate({ from, to, amount })).unwrap();

  const isLoading = buy.loaders.buyRequest || buy.loaders.buyRecheck;

  return (
    <div className={cn(null, [className])} data-testid={ti()}>
      <Converter getRate={getRate} {...converterState} />
      <Limits limits={wallet.limits.buy} {...converterState} />
      <Details from={fromCurrency} to={toCurrency} fromAmount={fromAmount} operationRate={rate} />
      <Button
        className={cn('button')}
        type="submit"
        onClick={onSubmit}
        disabled={CONST.operationsDisabled}
        isLoading={isLoading}
        testId={ti('button')}
      >
        {t('card.add_new_card')}
      </Button>
      {CONST.operationsDisabled && <OperationsDisabledDisclaimer />}
      <ZHDisclaimerInfo />
    </div>
  );
};

export default BuyWithNewCard;
